import React from 'react';

/** Flowex Ola A palette — no purple emoji marks. */
const ACCENTS = {
  guardian: '#2A9D6B',
  explorer: '#3d8bfd',
  visionary: '#E85D4C',
  income_builder: '#C9A961',
};

const ALIASES = {
  guardian: 'guardian',
  explorer: 'explorer',
  visionary: 'visionary',
  income_builder: 'income_builder',
  'income builder': 'income_builder',
  incomebuilder: 'income_builder',
};

/** Normalize API / UI labels → portfolio key. */
export function normalizePortfolioKey(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  // Strip currency suffix: "explorer puente", "guardian-raiz"
  const base = s.split(' ')[0];
  if (ALIASES[s]) return ALIASES[s];
  if (ALIASES[base]) return ALIASES[base];
  // Title-case / Pascal
  const compact = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (ACCENTS[compact]) return compact;
  if (compact.startsWith('income')) return 'income_builder';
  if (compact.startsWith('guard')) return 'guardian';
  if (compact.startsWith('explor')) return 'explorer';
  if (compact.startsWith('vision')) return 'visionary';
  return null;
}

export function portfolioAccent(key) {
  const k = normalizePortfolioKey(key);
  return (k && ACCENTS[k]) || ACCENTS.explorer;
}

function IconGuardian() {
  return (
    <svg viewBox="0 0 24 24" width="58%" height="58%" fill="none" aria-hidden="true">
      <path
        d="M12 3.5l7 2.5v5.2c0 4.3-2.9 7.8-7 9.3-4.1-1.5-7-5-7-9.3V6L12 3.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Brújula / compass — circle + needle. NOT a clock face. */
function IconExplorer() {
  return (
    <svg viewBox="0 0 24 24" width="58%" height="58%" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      {/* N tick */}
      <path d="M12 4.2v1.6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      {/* Needle pointing NE-ish (compass, not clock hands) */}
      <path
        d="M12 12 L15.6 8.4 L12 16.2 L8.4 15.6 Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="1.15" fill="currentColor" />
    </svg>
  );
}

function IconVisionary() {
  return (
    <svg viewBox="0 0 24 24" width="58%" height="58%" fill="none" aria-hidden="true">
      <path
        d="M4.5 16.5 L9 11.5 L12.5 14 L19.5 6.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* spark */}
      <path
        d="M18.2 4.8 L19.5 6.5 L21.2 5.4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M4.5 19.5 H19.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

function IconIncomeBuilder() {
  return (
    <svg viewBox="0 0 24 24" width="58%" height="58%" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 7.2v9.6M14.6 9.1c-.5-1-1.5-1.5-2.6-1.5-1.6 0-2.7 1-2.7 2.3 0 1.2.8 1.9 2.5 2.3l.9.2c1.7.4 2.6 1.1 2.6 2.4 0 1.4-1.2 2.4-3 2.4-1.3 0-2.4-.6-2.9-1.7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ICONS = {
  guardian: IconGuardian,
  explorer: IconExplorer,
  visionary: IconVisionary,
  income_builder: IconIncomeBuilder,
};

/**
 * Rounded square portfolio mark with white stroke SVG.
 * @param {object} props
 * @param {string} props.portfolio — key or display name
 * @param {number} [props.size=48]
 * @param {string} [props.className]
 * @param {object} [props.style]
 */
export default function PortfolioMark({ portfolio, size = 48, className = '', style, title }) {
  const key = normalizePortfolioKey(portfolio) || 'explorer';
  const accent = ACCENTS[key];
  const Icon = ICONS[key] || IconExplorer;
  const dim = typeof size === 'number' ? size : 48;
  const radius = Math.max(8, Math.round(dim * 0.25));

  return (
    <span
      className={`portfolio-mark ${className}`.trim()}
      title={title || key}
      style={{
        width: dim,
        height: dim,
        borderRadius: radius,
        backgroundColor: accent,
        ...style,
      }}
      role="img"
      aria-label={`Portafolio ${key}`}
    >
      <Icon />
    </span>
  );
}

export { ACCENTS as PORTFOLIO_ACCENTS };
